import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useScanBalance, type ScanBalance } from "@/hooks/useScanBalance";
import { TIERS, type Tier } from "@/lib/figVerifyTiers";
import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";
import { toast } from "@/lib/toast";
import { isNative } from "@/lib/capacitor";
import {
  TIER_TO_PACKAGE,
  getCurrentPackages,
  openManageSubscriptions,
  purchasePackage,
  restorePurchases,
  type RcPackage,
} from "@/lib/revenuecat";

// FIG Verify palette — bound to theme tokens so every theme reskins it.
const PLUM = "hsl(var(--ink))";
const PLUM_DARK = "hsl(var(--ink))";
const GOLD = "hsl(var(--marigold))";
const CREAM = "hsl(var(--cream))";

interface Props {
  /** When true, render in compact paywall layout (full-screen modal). */
  paywall?: boolean;
  onClose?: () => void;
}

export function SubscriptionTiers({ paywall, onClose }: Props) {
  const { user } = useAuth();
  const { balance, refresh } = useScanBalance();
  const [activePrice, setActivePrice] = useState<string | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  // Native (iOS) IAP state. On web these stay empty and Stripe owns checkout.
  const [rcPackages, setRcPackages] = useState<RcPackage[]>([]);
  const [iapBusyTier, setIapBusyTier] = useState<string | null>(null);

  const currentTier = balance?.subscriptionTier ?? "none";
  const PAID_TIERS = new Set(["collector", "seller", "pro_seller"]);
  const subActive =
    PAID_TIERS.has(currentTier) &&
    (balance?.subscriptionStatus === "active" || balance?.subscriptionStatus === "trialing");

  // Load RevenueCat packages on iOS. No-op on web.
  useEffect(() => {
    if (!isNative) return;
    let cancelled = false;
    (async () => {
      const pkgs = await getCurrentPackages();
      if (!cancelled && pkgs) setRcPackages(pkgs);
    })();
    return () => { cancelled = true; };
  }, []);

  // iOS purchase flow — kicks the native App Store sheet for the matching
  // RC package, then refreshes scan balance once the webhook has flipped
  // the tier in Supabase. Apple's review fails any "buy" UI that doesn't
  // surface their sheet, so we always route through Purchases.purchasePackage.
  const buyOnNative = async (tier: Tier) => {
    const pkgId = TIER_TO_PACKAGE[tier.id];
    const pkg = rcPackages.find((p) => p.identifier === pkgId);
    if (!pkg) {
      toast.error("That product isn't available right now. Try again in a moment.");
      return;
    }
    setIapBusyTier(tier.id);
    try {
      const ok = await purchasePackage(pkg);
      if (ok) {
        toast.success("Subscribed — welcome to FIG Verify.");
        // Give the RC webhook a beat to write subscription_tier, then refresh.
        setTimeout(() => { void refresh(); }, 1500);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Couldn't complete the purchase.";
      toast.error(msg);
    } finally {
      setIapBusyTier(null);
    }
  };

  const restoreOnNative = async () => {
    const restored = await restorePurchases();
    if (restored) {
      toast.success("Purchases restored.");
      setTimeout(() => { void refresh(); }, 1500);
    } else {
      toast.info("No purchases to restore.");
    }
  };

  const openPortal = async () => {
    if (!user) return;
    // On iOS, subscriptions are managed in the system Settings app — Apple
    // doesn't allow cancelling auto-renewable subs from inside the app.
    if (isNative) {
      await openManageSubscriptions();
      return;
    }
    setOpeningPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-portal-session", {
        body: {
          environment: getStripeEnvironment(),
          returnUrl: `${window.location.origin}/settings`,
        },
      });
      if (error || !data?.url) throw new Error(error?.message || "Couldn't open portal");
      window.open(data.url as string, "_blank", "noopener");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't open portal");
    } finally {
      setOpeningPortal(false);
    }
  };

  return (
    <div
      style={{
        background: paywall ? PLUM : "transparent",
        color: paywall ? CREAM : undefined,
        padding: paywall ? 20 : 0,
        minHeight: paywall ? "100%" : undefined,
      }}
    >
      {paywall && (
        <div style={{ marginBottom: 20, textAlign: "center" }}>
          <h1 style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', serif", fontSize: 32, fontWeight: 700, color: GOLD, margin: 0 }}>
            You've used your 10 free scans.
          </h1>
          <div style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', serif", fontSize: 18, marginTop: 6, color: CREAM }}>
            Ready to keep going?
          </div>
        </div>
      )}

      {!paywall && balance && (
        <ScanCounterPanel balance={balance} />
      )}

      {!paywall && subActive && (
        <div style={{ margin: "12px 0 16px" }}>
          <button
            type="button"
            onClick={openPortal}
            disabled={openingPortal}
            className="btn"
            style={{ padding: "8px 14px", cursor: openingPortal ? "wait" : "pointer" }}
          >
            {openingPortal ? "Opening…" : "Manage subscription"}
          </button>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 12,
        }}
      >
        {TIERS.map((t) => (
          <TierCard
            key={t.priceId}
            tier={t}
            isCurrent={subActive && currentTier === t.id}
            paywall={!!paywall}
            busy={iapBusyTier === t.id}
            onChoose={() => {
              if (isNative) void buyOnNative(t);
              else setActivePrice(t.priceId);
            }}
          />
        ))}
      </div>

      {/* Apple requires a Restore Purchases affordance on any screen
          that sells subscriptions. The button is iOS-only — web users
          recover via Stripe billing portal. */}
      {isNative && (
        <div style={{ marginTop: 12, textAlign: "center" }}>
          <button
            type="button"
            onClick={restoreOnNative}
            style={{
              background: "transparent",
              border: "none",
              color: paywall ? "rgba(246,239,226,0.85)" : "hsl(var(--ink-2))",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              cursor: "pointer",
              padding: 6,
              textDecoration: "underline",
            }}
          >
            Restore purchases
          </button>
        </div>
      )}

      <div
        style={{
          marginTop: 14,
          textAlign: "center",
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10,
          color: paywall ? "rgba(246,239,226,0.7)" : "hsl(var(--ink-2))",
          letterSpacing: "0.05em",
          lineHeight: 1.5,
        }}
      >
        Scans reset each billing cycle. Cancel anytime.
        {/* Apple guideline 3.1.2 auto-renew disclosure — required on
            any UI that offers auto-renewable subscriptions on iOS. The
            wording mirrors Apple's recommended language so an App
            Review pass on the IAP page is straightforward. */}
        <br /><br />
        Subscriptions auto-renew until cancelled. Your iTunes account
        will be charged at confirmation of purchase and within 24 hours
        before the end of the current period unless auto-renew is
        turned off at least 24 hours prior. Manage or cancel in
        Settings → Apple ID → Subscriptions after purchase.
        {" "}
        <a
          href="/terms"
          style={{
            color: paywall ? "rgba(246,239,226,0.85)" : "hsl(var(--ink-2))",
            textDecoration: "underline",
          }}
        >
          Terms
        </a>
        {" · "}
        <a
          href="/privacy"
          style={{
            color: paywall ? "rgba(246,239,226,0.85)" : "hsl(var(--ink-2))",
            textDecoration: "underline",
          }}
        >
          Privacy
        </a>
      </div>

      {paywall && onClose && (
        <button
          type="button"
          onClick={onClose}
          style={{
            marginTop: 14,
            width: "100%",
            padding: "10px",
            background: "transparent",
            border: `1px solid ${GOLD}`,
            color: GOLD,
            cursor: "pointer",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          Maybe later
        </button>
      )}

      {/* Inline checkout */}
      {activePrice && (
        <CheckoutOverlay
          priceId={activePrice}
          onClose={() => { setActivePrice(null); void refresh(); }}
        />
      )}
    </div>
  );
}

function TierCard({
  tier, isCurrent, paywall, busy, onChoose,
}: { tier: Tier; isCurrent: boolean; paywall: boolean; busy?: boolean; onChoose: () => void }) {
  return (
    <div
      style={{
        position: "relative",
        background: paywall ? PLUM_DARK : "hsl(var(--paper))",
        border: tier.popular ? `2px solid ${GOLD}` : `1px solid ${paywall ? "rgba(212,168,74,0.3)" : "hsl(var(--ink))"}`,
        padding: 14,
        color: paywall ? CREAM : "hsl(var(--ink))",
      }}
    >
      {tier.popular && (
        <div
          style={{
            position: "absolute", top: -10, right: 10,
            background: GOLD,
            // Cream on teal — never berry-on-teal.
            color: "hsl(var(--marigold-fg))",
            padding: "2px 8px",
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
            letterSpacing: "0.12em", textTransform: "uppercase",
          }}
        >
          Most popular
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <div style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700 }}>
          {tier.name}
        </div>
        <div style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, color: paywall ? CREAM : "hsl(var(--ink))" }}>
          {tier.priceLabel}
          <span style={{ fontSize: 12, fontWeight: 400, opacity: 0.7 }}> {tier.cadence}</span>
        </div>
      </div>
      <div
        style={{
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
          letterSpacing: "0.08em", textTransform: "uppercase",
          opacity: 0.75, marginTop: 4,
        }}
      >
        {tier.scans} scans{tier.recurring ? "/mo" : ""} · {tier.perScan}
      </div>
      <ul style={{ margin: "10px 0 12px", paddingLeft: 16, fontSize: 12, lineHeight: 1.5 }}>
        {tier.bullets.map((b) => <li key={b}>{b}</li>)}
      </ul>
      {isCurrent ? (
        <div
          style={{
            padding: "8px 12px",
            border: `1px solid ${GOLD}`,
            color: GOLD,
            textAlign: "center",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
          }}
        >
          Current plan
        </div>
      ) : (
        <button
          type="button"
          onClick={onChoose}
          disabled={busy}
          style={{
            width: "100%",
            padding: "10px 12px",
            background: tier.popular ? GOLD : (paywall ? "transparent" : "hsl(var(--ink))"),
            // On the popular tier (teal button) the label must be cream,
            // not berry — same rule as the badge above. On non-popular
            // tiers the button is either ink (cream text) or transparent
            // (teal outline + teal label on paper, which is fine).
            color: tier.popular ? "hsl(var(--marigold-fg))" : (paywall ? GOLD : "hsl(var(--ink-fg))"),
            border: tier.popular ? "none" : (paywall ? `1px solid ${GOLD}` : "none"),
            cursor: busy ? "wait" : "pointer",
            opacity: busy ? 0.65 : 1,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
          }}
        >
          {busy ? "Opening…" : (tier.recurring ? "Subscribe" : "Buy pack")}
        </button>
      )}
    </div>
  );
}

function CheckoutOverlay({ priceId, onClose }: { priceId: string; onClose: () => void }) {
  const { user } = useAuth();
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 12,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "hsl(var(--paper))",
          width: "100%", maxWidth: 480, maxHeight: "90vh", overflow: "auto",
          border: "1px solid hsl(var(--ink))",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderBottom: "1px dashed hsl(var(--sand-deep))" }}>
          <div className="caps" style={{ fontSize: 11 }}>Checkout</div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18 }}>×</button>
        </div>
        <StripeEmbeddedCheckout
          priceId={priceId}
          customerEmail={user?.email ?? undefined}
          userId={user?.id}
        />
      </div>
    </div>
  );
}

const PAID_TIERS_SET = new Set(["collector", "seller", "pro_seller"]);

function ScanCounterPanel({ balance }: { balance: ScanBalance }) {
  const isPaidSub =
    PAID_TIERS_SET.has(balance.subscriptionTier) &&
    (balance.subscriptionStatus === "active" || balance.subscriptionStatus === "trialing");
  const free = balance.freeRemaining;
  const credits = balance.scanCredits;

  let displayCount: number;
  let label: string;
  let sublabel: string | null = null;
  let badge: string | null = null;
  let accent = false;

  if (balance.trialActive) {
    displayCount = free + credits;
    label = "scans remaining";
    sublabel = `${displayCount} scan${displayCount === 1 ? "" : "s"} + Pro Seller features`;
    badge = "TRIAL";
    accent = true;
  } else if (free > 0) {
    displayCount = free;
    label = "scans remaining";
    sublabel = "Full Pro experience on the house.";
    accent = true;
  } else if (isPaidSub) {
    displayCount = balance.subscriptionMonthlyCap + credits;
    label = "scans remaining";
    sublabel = `${balance.subscriptionMonthlyCap} scans per month`;
  } else {
    displayCount = credits;
    label = credits === 1 ? "scan remaining" : "scans remaining";
  }

  return (
    <div
      style={{
        background: accent ? PLUM : "hsl(var(--paper-3))",
        color: accent ? CREAM : "hsl(var(--ink))",
        padding: "16px 18px",
        marginBottom: 16,
        border: accent ? `1px solid ${GOLD}` : "1px solid hsl(var(--ink))",
        position: "relative",
      }}
    >
      {badge && (
        <div
          style={{
            position: "absolute",
            top: -10,
            right: 10,
            background: GOLD,
            // Text on the teal badge is cream — never berry, which would
            // produce berry-on-teal in themes where GOLD resolves to teal
            // (Trapper Keeper). --marigold-fg auto-derives to the brightest
            // safe neutral, which is cream on a teal surface.
            color: "hsl(var(--marigold-fg))",
            padding: "2px 8px",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
          }}
        >
          {badge}
        </div>
      )}
      <div
        style={{
          fontFamily: "'Fraunces', 'Cormorant Garamond', serif",
          fontWeight: 800,
          fontSize: 52,
          lineHeight: 1,
          // Big number sits ON the berry card (PLUM = --ink) — must be
          // cream, never teal. Teal-on-berry was the clash the user flagged.
          color: accent ? CREAM : "hsl(var(--ink))",
          letterSpacing: "-0.02em",
        }}
      >
        {displayCount}
      </div>
      <div
        style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          marginTop: 4,
          opacity: 0.8,
        }}
      >
        {label}
      </div>
      {sublabel && (
        <div
          style={{
            fontFamily: "'Fraunces', 'Cormorant Garamond', serif",
            fontSize: 13,
            marginTop: 6,
            opacity: 0.75,
            lineHeight: 1.3,
          }}
        >
          {sublabel}
        </div>
      )}
    </div>
  );
}